import {createAction, createAsyncThunk, createReducer} from '@reduxjs/toolkit';

import {ACCESSORIES_TABS} from 'constants/accessory';
import {ENDPOINTS} from 'constants/endpoints';
import {apiConfig, HTTP_REQUEST_HEADER, HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE} from 'libs/api/api';

import {getQuote, removeQuoteItem} from '../../reducers/quoteItems/quoteItems';

import {changeItemQtyAction} from '../changeItemQty/changeItemQty';

import {AccessoryType, AccessoryGroupType, AccessoriesStateType} from './accessoriesType';

export const initialState: AccessoriesStateType = {
    accessoryLoadingId: '',
    addingId: '',
    availableTabs: [],
    selectedTab: ACCESSORIES_TABS.ESSENTIALS,
    simpleProducts: [],
    windowProducts: [],
};

/**
 * Fetch accessories data by window accessories ids
 * @param {Array<string>} ids - window accessories ids
 * @return {ActionType<AxiosPromise> } - response promise
 */
export const fetchAccessories = createAsyncThunk<
    {
        data: AccessoryGroupType[];
    },
    Array<number | string>
>('CART@FETCH_ACCESSORIES', ids =>
    apiConfig.instanceGusaBackend({
        headers: {
            [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
        },
        method: 'get',
        params: {ids},
        url: `${ENDPOINTS.CART_GET_ACCESSORIES}`,
    }),
);

/**
 * Add accessory to cart
 * @param {string} productId - accessory productId
 * @return {ActionType<AxiosPromise> } - response promise
 */
export const addAccessory = createAsyncThunk<void, string>('CART@ADD_ACCESSORY', productId => {
    const data = JSON.stringify({productId});
    return apiConfig.instanceGusaBackend({
        data,
        headers: {
            [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.APPLICATION_JSON,
        },
        method: 'post',
        url: ENDPOINTS.ADD_TO_CART_V2,
    });
});

export const changeSelectedTab = createAction<ACCESSORIES_TABS>('CART@CHANGE_SELECTED_TAB');

/**
 * Get cart accessories data
 */
export const accessories = createReducer<typeof initialState>(initialState, builder =>
    builder
        .addCase(removeQuoteItem.fulfilled, (state, action) => ({
            ...state,
            accessoryLoadingId: '',
            windowProducts: action.payload.data.accessories || initialState.windowProducts,
        }))
        .addCase(getQuote.fulfilled, (state, action) => ({
            ...state,
            accessoryLoadingId: '',
            windowProducts: action.payload.data.accessories || initialState.windowProducts,
        }))
        .addCase(fetchAccessories.fulfilled, (state, action) => {
            const availableTabs: ACCESSORIES_TABS[] = [];

            const simpleProducts = action.payload.data.reduce((accumulator, item) => {
                if (item.essentials && !availableTabs.includes(ACCESSORIES_TABS.ESSENTIALS)) {
                    availableTabs.unshift(ACCESSORIES_TABS.ESSENTIALS);
                }

                if (item.accessories_group && !availableTabs.includes(item.accessories_group)) {
                    availableTabs.push(item.accessories_group);
                }

                return [
                    ...accumulator,
                    ...item.children.map(product => ({
                        ...product,
                        accessories_group: item.accessories_group,
                        essentials: item.essentials,
                        parentId: item.id,
                    })),
                ];
            }, [] as AccessoryType[]);

            return {...state, availableTabs, simpleProducts};
        })
        .addCase(removeQuoteItem.pending, (state, action) => ({
            ...state,
            accessoryLoadingId: action.meta.arg.item.product_id,
        }))
        .addCase(changeSelectedTab, (state, action) => ({
            ...state,
            selectedTab: action.payload,
        }))
        .addCase(changeItemQtyAction.pending, (state, action) => ({
            ...state,
            accessoryLoadingId: action.meta.arg.productId as string,
        }))
        .addCase(addAccessory.pending, (state, action) => ({
            ...state,
            accessoryLoadingId: action.meta.arg,
            addingId: action.meta.arg,
        }))
        .addCase(addAccessory.fulfilled, state => ({...state, addingId: ''})),
);
